/**
 * <srp-infinite-scroll> — search results page infinite scroll + GA4
 * view_item_list (Search Bar PRD 4.6 / 7.2).
 *
 * Lives inside #product-grid (sections/main-search.liquid), right where the
 * pagination fallback renders. As it scrolls into view it fetches
 * paginate.next.url — query and sort ride along in the URL — and appends the
 * next page's grid items to the current grid. facets.js sort/filter updates
 * replace #ProductGridContainer wholesale, which disconnects this element and
 * connects the freshly rendered one carrying the correct next-url, so state
 * never goes stale.
 */
/**
 * Client-side re-sort of the loaded SRP grid for the sorts Shopify search can't
 * do server-side:
 *  - 'shown-price-ascending' / 'shown-price-descending' — Price Low/High over the
 *    SHOWN (metafield-discounted) price, used on a price-filtered SRP where the
 *    result set itself is client-side (see SrpSort.priceMode).
 *  - 'new-arrivals' (newest created_at) / 'best-deals' (highest discount %) —
 *    withdrawn options, kept for reference.
 * Reorders only the products already in the DOM.
 */
function srpSortGrid(grid, key) {
  if (!grid) return;
  const items = [...grid.querySelectorAll('li[data-product-id]')];
  if (!items.length) return;

  const priceDirection =
    key === 'shown-price-ascending' ? 1 : key === 'shown-price-descending' ? -1 : 0;

  let compare;
  if (priceDirection) {
    // Two orderings the grid already has and a price sort must not undo:
    // items the price filter hid go last, and PRD 3.5 keeps out-of-stock
    // products below in-stock ones. Price only decides ties within a bucket.
    const rank = (li) =>
      (li.style.display === 'none' ? 2 : 0) +
      (li.classList.contains('grid__item--srp-oos') ? 1 : 0);
    const price = (li) => {
      const value = parseFloat(li.dataset.shownPrice);
      return isFinite(value) ? value : null;
    };
    compare = (a, b) => {
      if (rank(a) !== rank(b)) return rank(a) - rank(b);
      const priceA = price(a);
      const priceB = price(b);
      if (priceA === null || priceB === null) return priceA === priceB ? 0 : priceA === null ? 1 : -1;
      return (priceA - priceB) * priceDirection;
    };
  } else {
    const value = (li) =>
      key === 'best-deals' ? Number(li.dataset.discount) || 0 : Number(li.dataset.createdAt) || 0;
    compare = (a, b) => value(b) - value(a);
  }

  items.sort(compare).forEach((li) => grid.appendChild(li));
}

/** Shopify sort_by -> the equivalent client-side key, for price mode only. */
const SRP_PRICE_SORT_KEYS = {
  'price-ascending': 'shown-price-ascending',
  'price-descending': 'shown-price-descending',
};

/**
 * The current SRP URL with sort_by swapped (or dropped when `value` is null).
 *
 * The result set is defined by more than `q`: a price search ("under ₹500")
 * rides on sb_dgte/sb_dlte/sb_prep, and facets ride on filter.*. The
 * server-rendered sort links only carry q/type/options[prefix], so following one
 * dropped all of that — a pure price search (q=*, everything narrowed
 * client-side) came back as the WHOLE catalog in price order, and a facet-
 * filtered search lost its facets. Copying the live query string keeps a sort a
 * pure reorder of the same results.
 */
function srpSortHref(value) {
  const params = new URLSearchParams(window.location.search);
  if (value) params.set('sort_by', value);
  else params.delete('sort_by');
  params.delete('page'); // a new sort restarts at page 1
  return `${window.location.pathname}?${params.toString()}`;
}

class SrpInfiniteScroll extends HTMLElement {
  connectedCallback() {
    this.grid = this.closest('#product-grid')?.querySelector('ul.product-grid');
    this.fetching = false;
    this.classList.add('is-enhanced');

    // view_item_list for the initially rendered page (item_list_name: srp).
    if (this.grid) {
      this.trackItems(this.grid.querySelectorAll('li[data-product-id]'), 0);
    }

    if (!this.grid) return;

    // GA4 view_item (PRD 7.2): stash the tapped product so the PDP it opens
    // fires view_item on load with item_list_name: srp + its 1-based grid
    // position. Paired with fireSearchViewItem() in nav-search-toggle.js.
    this.grid.addEventListener('click', (event) => {
      const li = event.target.closest?.('li[data-product-id]');
      if (!li || !this.grid.contains(li)) return;
      const lis = Array.from(this.grid.querySelectorAll('li[data-product-id]'));
      try {
        sessionStorage.setItem(
          'sammmm:search:view_item',
          JSON.stringify({
            item_id: li.dataset.productId || '',
            item_name: li.dataset.productTitle || '',
            item_list_name: 'srp',
            index: lis.indexOf(li) + 1 || 1,
            ts: Date.now(),
          })
        );
      } catch (error) {
        // never break the tap
      }
    });

    // Committed SRP client-side handling:
    //  - priceFilter (sb_dgte/sb_dlte): a "under 500" / "over 1000" search. The
    //    store shows a metafield discount Shopify can't sort/filter on, so we
    //    filter by the SHOWN price. Matches sit on any page → eager-load all.
    //  - name queries: show Shopify's results AS RETURNED. Shopify's search
    //    already applies the store's Search & Discovery synonyms + boosts, so we
    //    no longer re-filter client-side against the field allowlist — that used
    //    to drop synonym-only matches (e.g. "icecream" -> Poutie). Trade-off:
    //    description-only matches Shopify returns are no longer stripped.
    this.priceFilter = this.readPriceFilter();
    this.nameTerm = this.readNameTerm();

    // Running header count. main-search.liquid already subtracted THIS page's
    // PRD 3.4 exclusions from Shopify's results_count, but results_count spans
    // every page while the pre-pass only sees one — so each page loaded below
    // subtracts its own data-excluded and the number converges on the real total.
    // Price mode owns the count outright (updateCount with the shown-price
    // filtered total), so it opts out of the running tally.
    this.runningCount = parseInt(this.countElement()?.textContent || '', 10);
    if (!isFinite(this.runningCount)) this.runningCount = null;

    if (this.priceFilter) {
      this.autoLoadAll(); // eager-load every page, then filter by price
      return;
    }

    this.enableLazyScroll();
  }

  /** The "Search: <N> Products found" number, kept in its own span so updating
   *  it never eats the " for 'term'" suffix beside it. */
  countElement() {
    return document.querySelector('.srp-breadcrumb [data-srp-count]');
  }

  enableLazyScroll() {
    if (!this.dataset.nextUrl || !this.grid) return;
    this.observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) this.loadNext();
      },
      { rootMargin: '400px 0px' }
    );
    this.observer.observe(this);
  }

  /** Discounted-price bounds from the URL (sb_dgte/sb_dlte), or null. */
  readPriceFilter() {
    const p = new URLSearchParams(window.location.search);
    const lte = p.get('sb_dlte');
    const gte = p.get('sb_dgte');
    if (!lte && !gte) return null;
    return { gte: gte ? parseInt(gte, 10) : 1, lte: lte ? parseInt(lte, 10) : null };
  }

  /** The typed product term for name-matching: q with its trailing prefix "*"
   *  removed. Empty for the pure price / match-all token "*". */
  readNameTerm() {
    const q = (new URLSearchParams(window.location.search).get('q') || '').trim();
    if (!q || q === '*') return '';
    return q.replace(/\*+$/, '').trim();
  }

  /** True when the given text (a product's allowlist search text, or its title
   *  fallback) matches the query: every typed word is the prefix of a word in
   *  the text. Mirrors the overlay's nav-search-toggle.js rule so the two agree. */
  titleMatchesQuery(title, query) {
    const q = (query || '').toLowerCase().trim();
    if (!q) return false;
    const titleWords = (title || '').toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);
    if (!titleWords.length) return false;
    return q
      .split(/\s+/)
      .filter(Boolean)
      .every((token) => titleWords.some((word) => word.startsWith(token)));
  }

  /** How many loaded products match the query on the ALLOWLISTED fields. */
  countFieldMatches() {
    if (!this.grid || !this.nameTerm) return 0;
    let n = 0;
    this.grid.querySelectorAll('li[data-product-id]').forEach((li) => {
      if (this.titleMatchesQuery(li.dataset.searchText || li.dataset.productTitle || '', this.nameTerm)) n += 1;
    });
    return n;
  }

  /**
   * Show/hide loaded items by the price filter; return how many show.
   * Price: data-shown-price (the MIN shown price main-search.liquid emits, so a
   * product with a cheap in-range variant is kept even when the card displays a
   * pricier default) in range. Name matching is delegated to Shopify (synonyms
   * + boosts), so there is no client-side field re-filter.
   */
  applyFilters() {
    if (!this.grid) return 0;
    const items = [...this.grid.querySelectorAll('li[data-product-id]')];
    let visible = 0;
    items.forEach((li) => {
      let ok = true;
      if (this.priceFilter) {
        let value = parseFloat(li.dataset.shownPrice);
        if (!isFinite(value)) {
          const now = li.querySelector('.pvc__price-now');
          // Keep the decimal: "₹852.50" -> 852.5, not 85250 (commas already stripped).
          value = now ? parseFloat(now.textContent.replace(/[^\d.]/g, '')) : NaN;
        }
        const { gte, lte } = this.priceFilter;
        ok = isFinite(value) && value >= (gte || 1) && (lte == null || value <= lte);
      }
      // Name matching is delegated to Shopify (synonyms + boosts) — no client-side
      // field re-filter here (see connectedCallback).
      li.style.display = ok ? '' : 'none';
      if (ok) visible += 1;
    });
    return visible;
  }

  /** Price mode: pull every page (bounded), filtering after each. */
  autoLoadAll() {
    this.applyFilters();
    let guard = 0;
    const step = () => {
      if (!this.dataset.nextUrl || guard >= 25) {
        this.updateCount(this.applyFilters());
        this.reapplySort();
        return;
      }
      guard += 1;
      this.loadNext().then(() => {
        this.applyFilters();
        // A client sort chosen mid-load ranks hidden items last, so re-run it
        // once this page's cards have been shown/hidden.
        this.reapplySort();
        step();
      });
    };
    step();
  }

  /** Re-apply the active client-side sort (if any) to the loaded grid. */
  reapplySort() {
    if (this.grid?.dataset.clientSort) srpSortGrid(this.grid, this.grid.dataset.clientSort);
  }

  /** Field-match mode: keep pulling pages while each adds new field matches
   *  (Shopify ranks them first, so once a page adds none they're exhausted),
   *  filtering as we go. A query that matches no allowed field ends with 0
   *  shown — there is no show-all fallback. */
  autoLoadMatches() {
    this.applyFilters();
    let guard = 0;
    let seen = this.countFieldMatches();
    const step = () => {
      if (!this.dataset.nextUrl || guard >= 25) {
        this.updateCount(this.applyFilters());
        return;
      }
      guard += 1;
      this.loadNext().then(() => {
        const now = this.countFieldMatches();
        this.applyFilters();
        if (now > seen) {
          seen = now;
          step();
        } else {
          this.updateCount(this.applyFilters());
        }
      });
    };
    step();
  }

  /** Correct "Search: N Products found" to the shown-price-filtered count. */
  updateCount(visible) {
    const count = this.countElement();
    if (count) {
      count.textContent = visible;
      return;
    }
    // No count span (older markup): fall back to rewriting the whole status,
    // which loses the " for 'term'" suffix.
    const status = document.querySelector('.srp-breadcrumb [role="status"]');
    if (status) status.textContent = `Search: ${visible} Products found`;
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  loadNext() {
    if (this.fetching || !this.dataset.nextUrl) return Promise.resolve();
    this.fetching = true;
    this.setAttribute('loading', '');

    return fetch(this.dataset.nextUrl)
      .then((response) => {
        if (!response.ok) throw new Error(response.status);
        return response.text();
      })
      .then((text) => {
        const doc = new DOMParser().parseFromString(text, 'text/html');
        const newItems = [...doc.querySelectorAll('#product-grid ul.product-grid > li')];
        const offset = this.grid.querySelectorAll('li[data-product-id]').length;
        newItems.forEach((item) => this.grid.appendChild(item));
        // Keep a client-side sort (New Arrivals / Best Deals) applied as pages load.
        if (this.grid.dataset.clientSort) srpSortGrid(this.grid, this.grid.dataset.clientSort);
        this.trackItems(
          newItems.filter((li) => li.dataset.productId),
          offset
        );

        const fetchedElement = doc.querySelector('srp-infinite-scroll');

        // Subtract the products this page's PRD 3.4 pre-pass dropped (they are
        // counted in Shopify's results_count but never rendered). Skipped in
        // price mode, which sets the count from its own filtered total.
        if (this.runningCount != null && !this.priceFilter) {
          const excluded = parseInt(fetchedElement?.dataset.excluded || '', 10);
          if (isFinite(excluded) && excluded > 0) {
            this.runningCount = Math.max(0, this.runningCount - excluded);
            this.updateCount(this.runningCount);
          }
        }

        const nextUrl = fetchedElement?.dataset.nextUrl;
        if (nextUrl) {
          this.dataset.nextUrl = nextUrl;
        } else {
          delete this.dataset.nextUrl;
          this.observer?.disconnect();
        }
      })
      .catch(() => {
        // Keep nextUrl so a later scroll retries; the pagination links inside
        // this element remain the hard fallback.
      })
      .finally(() => {
        this.fetching = false;
        this.removeAttribute('loading');
      });
  }

  trackItems(lis, offset) {
    const items = [...lis]
      .map((li, i) => ({
        item_id: li.dataset.productId || '',
        item_name: li.dataset.productTitle || '',
        index: offset + i + 1,
      }))
      .filter((item) => item.item_id);
    if (!items.length) return;

    const payload = {
      item_list_name: 'srp',
      items,
      user_type: this.dataset.userType || 'logged_out',
      timestamp: new Date().toISOString(),
    };
    // Route through the ONE global sender (snippets/ga4-base.liquid) so SRP
    // events are traced in the same place as the overlay's.
    if (typeof window.gtagEvent === 'function') {
      window.gtagEvent('view_item_list', payload);
    } else {
      try {
        if (typeof window.gtag === 'function') {
          window.gtag('event', 'view_item_list', payload);
        } else if (Array.isArray(window.dataLayer)) {
          window.dataLayer.push({ event: 'view_item_list', ...payload });
        }
      } catch (error) {
        // Analytics must never break the results page.
      }
    }
  }
}

customElements.define('srp-infinite-scroll', SrpInfiniteScroll);

/**
 * <srp-sort> — the SRP's Sort By dropdown (Figma 1237:3778 / 1237:4383).
 * Native options (Popularity / Price) are plain links carrying ?sort_by=, so
 * selection is a normal navigation — over the SAME results, because their hrefs
 * are rebuilt here from the live query string (srpSortHref). Two exceptions
 * reorder the loaded grid in place instead of navigating: a price search's
 * Price Low/High (see priceMode below), and New Arrivals / Best Deals
 * ([data-srp-client-sort], withdrawn) which have no native search sort.
 */
class SrpSort extends HTMLElement {
  connectedCallback() {
    this.toggle = this.querySelector('[data-srp-sort-toggle]');
    this.panel = this.querySelector('[data-srp-sort-panel]');
    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.onKeyUp = this.onKeyUp.bind(this);
    this.toggle?.addEventListener('click', () => this.setOpen(this.panel.hidden));

    // Re-point the server-rendered hrefs at the live query string, so a sort (or
    // Clear) keeps the price bounds / facets that scope the results.
    this.querySelectorAll('[data-srp-sort-value]').forEach((option) => {
      option.href = srpSortHref(option.dataset.srpSortValue);
    });
    const clear = this.querySelector('[data-srp-sort-clear]');
    if (clear) clear.href = srpSortHref(null);

    // Price mode: on a price search ("under ₹500") Shopify returns the full
    // matching set and srp-infinite-scroll.js narrows it by SHOWN price, so
    // Price Low/High must stay client-side over that narrowed set. Navigating
    // instead would re-run q=* unnarrowed — the whole catalog — and order by the
    // REGULAR price, which for a metafield-discounted product isn't the price on
    // the card (the same reason predictive-search.js's buildPriceUrl sets no
    // sort_by). Popularity still navigates: it's the default order, and the
    // rewritten href keeps the bounds.
    const params = new URLSearchParams(window.location.search);
    this.priceMode = params.has('sb_dlte') || params.has('sb_dgte');

    this.panel?.addEventListener('click', (event) => {
      // New Arrivals / Best Deals (withdrawn) reorder the loaded grid too.
      const option = event.target.closest('[data-srp-client-sort], [data-srp-sort-value]');
      if (!option) return;
      const key =
        option.dataset.srpClientSort ||
        (this.priceMode ? SRP_PRICE_SORT_KEYS[option.dataset.srpSortValue] : null);
      if (!key) return; // native sort outside price mode: plain navigation
      event.preventDefault();
      const grid = document.querySelector('#product-grid ul.product-grid');
      if (grid) {
        // Stored so pages still loading re-apply it as they append (loadNext).
        grid.dataset.clientSort = key;
        srpSortGrid(grid, key);
      }
      this.querySelectorAll('.srp-sort__option').forEach((o) => o.classList.remove('is-active'));
      this.querySelectorAll('[role="option"]').forEach((li) => li.setAttribute('aria-selected', 'false'));
      option.classList.add('is-active');
      option.closest('[role="option"]')?.setAttribute('aria-selected', 'true');
      this.setOpen(false);
    });
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('keyup', this.onKeyUp);
  }

  setOpen(open) {
    if (!this.panel || !this.toggle) return;
    this.panel.hidden = !open;
    this.toggle.setAttribute('aria-expanded', open);
    this.classList.toggle('is-open', open);
    if (open) {
      document.addEventListener('click', this.onDocumentClick);
      document.addEventListener('keyup', this.onKeyUp);
    } else {
      document.removeEventListener('click', this.onDocumentClick);
      document.removeEventListener('keyup', this.onKeyUp);
    }
  }

  onDocumentClick(event) {
    if (!this.contains(event.target)) this.setOpen(false);
  }

  onKeyUp(event) {
    if (event.code?.toUpperCase() === 'ESCAPE') this.setOpen(false);
  }
}

customElements.define('srp-sort', SrpSort);
